import { Injectable, WritableSignal, inject, signal } from '@angular/core';

import { Fai, Phone, Plan, Product, ProductFactory, QuoteModel, Sim } from '@bytel/bytel-sales';
import { CartCheckoutService, CheckoutCartModel } from '@bytel/checkout';

import { firstValueFrom, lastValueFrom } from 'rxjs';

import { ISim, SIM_TYPES } from '../interfaces/sim.interace';
import { SimContextModel } from '../models/sim-context.model';
import { SimRepository } from '../repositories/sim.repository';
import { ConfigurationService } from './configuration.service';

@Injectable({
    providedIn: 'root',
})
export class SimService {
    public simContexts: WritableSignal<SimContextModel[]> = signal([]);

    protected checkoutCartService: CartCheckoutService = inject(CartCheckoutService);
    protected simRepository: SimRepository = inject(SimRepository);
    protected configService: ConfigurationService = inject(ConfigurationService);

    public async getSimContext(cartModel: CheckoutCartModel): Promise<void> {
        if (!this.shouldUpdateSim(cartModel)) {
            this.simContexts.set([]);
            return;
        }
        const contexts = await lastValueFrom(this.simRepository.GetSimContext(cartModel));
        this.simContexts.set(contexts);
    }

    public updateSimPrices(cartModel: CheckoutCartModel, quoteIndex: number): void {
        if (!this.shouldUpdateSim(cartModel)) {
            return;
        }

        const currentSim = cartModel.getQuote(quoteIndex).getProductByType(Sim);
        if (!currentSim) {
            return;
        }

        const sim = this.simContexts()[quoteIndex]?.sims.find((s) => s.gencode === currentSim.gencode);
        if (sim && sim.price >= 0) {
            currentSim.setPortabilityPrices({
                aUnePortabilite: { price: sim.price, type: sim.priceType },
                nAPasDePortabilite: { price: sim.price, type: sim.priceType },
            });
        }
    }

    public async updateSim(cartModel: CheckoutCartModel, quoteIndex: number, product: Product): Promise<void> {
        if (cartModel.isCartEmpty()) {
            return;
        }
        const quote = this.checkoutCartService.getQuote(cartModel, quoteIndex);
        if (quote.getProductByType(Plan)) {
            await this._handleCurrentSimContext(cartModel, quote, product, quoteIndex);
        } else {
            return;
        }
    }

    private async _handleCurrentSimContext(
        cartModel: CheckoutCartModel,
        quote: QuoteModel,
        product: Product,
        quoteIndex: number,
    ): Promise<Sim> {
        const currentContext = this.simContexts().filter((context) => context.isCurrent)[quoteIndex];
        if (quote.isRenewal() && currentContext?.isCompatible) {
            return null;
        }

        const preferedSim = this.getPreferedSim(currentContext, quote);
        let currentSim = quote.getProductByType(Sim);
        const hasPhone = quote.getProductByType(Phone);

        if (
            !ProductFactory.Is(product, Sim) &&
            preferedSim &&
            (!currentSim || (hasPhone && !this.isSimCompatibility(this.simContexts(), currentSim.gencode)))
        ) {
            currentSim = (await firstValueFrom(
                this.checkoutCartService.addProductByGencode(cartModel, preferedSim.gencode, quoteIndex, false),
            )) as Sim;
        }

        const simAdded = currentContext?.sims.find((sim) => sim.gencode === currentSim.gencode);
        if (simAdded && simAdded.price >= 0) {
            currentSim.setPortabilityPrices({
                aUnePortabilite: { price: simAdded.price, type: simAdded.priceType },
                nAPasDePortabilite: { price: simAdded.price, type: simAdded.priceType },
            });

            await this.checkoutCartService.refresh(cartModel, quoteIndex);
            await this.checkoutCartService.updateFunding(cartModel, quoteIndex);
        }

        return currentSim;
    }

    private getPreferedSim(context: SimContextModel, quote: QuoteModel): ISim {
        const currentPlan = quote.getProductByType<Plan>(Plan);
        const isSummerEdition =
            currentPlan?.gencode === this.configService.getData()?.global?.specialOffers?.summerEdition;
        const eSim = context.sims.find((sim) => sim.type === SIM_TYPES.QRESIM);
        const preferedSim = context.sims.find((sim) => sim.isPrefered);
        const fallbackSim = context.sims.find((sim) => sim.type === SIM_TYPES.SIM);
        const defaultSim = preferedSim || fallbackSim;
        return isSummerEdition ? eSim : defaultSim;
    }

    private isSimCompatibility(contexts: SimContextModel[], gencodeToCheck: string): boolean {
        return contexts.find((context) => context.isCurrent)?.isSimCompatible(gencodeToCheck);
    }

    private shouldUpdateSim(cartModel: CheckoutCartModel): boolean {
        if (
            cartModel.getQuote().isRenewal() &&
            !cartModel.getQuote(0)?.products?.some((p) => ProductFactory.Is(p, Phone))
        ) {
            return false;
        }

        // Sometimes quote has the wrong type
        // Until we figure out why this check will avoid unnecessary calls
        if (
            cartModel.getQuote(0).isAcquisitionFix() &&
            !ProductFactory.Is(cartModel.getQuote(0).getPrincipalProduct(Fai), Fai, false)
        ) {
            return false;
        }

        if (cartModel.isCartEmpty() || cartModel.isTelOnly() || cartModel.isFaiOnly() || cartModel.isAccessoryOnly()) {
            return false;
        }

        return true;
    }
}
